import axios, { AxiosRequestConfig } from "axios";
import identity from "lodash/identity";
import pickBy from "lodash/pickBy";

import getErrorDetails from "../../../api/helpers/getErrorDetails";
import paramsSerializer from "../../../api/helpers/paramsSerializer";
import { portalApi, TAGS } from "../../../api/rtkApi";
import { DEFAULT_PAGINATION } from "../../../constants/defaultPagination";
import { returnProperCashManagementProviderAccountObject } from "../utils/returnProperCashManagementProviderAccountObject";

import type { UUID } from "../../../utils";
import type { ObjectWithId } from "../../../utils/ObjectWithId";
import type { TranslationKeysFromBackend } from "../../../utils/types";
import type { TRANSLATION_KEYS } from "../constants/TranslationKeys";
import type {
  CashManagementAccount,
  SettlementScheduleConfig,
} from "../models/CashManagement";
import type { CashManagementProvider } from "../models/Provider";

export type GetCashManagementAccounts = (
  id: UUID,
  active: boolean | undefined,
  options?: AxiosRequestConfig
) => Promise<{ list: CashManagementAccount[] }>;

export type GetCashManagementAccount = (
  id: UUID,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PostCashManagementAccount = (
  id: UUID,
  data: CashManagementAccount,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type PatchCashManagementAccount = (
  id: UUID,
  data: Partial<CashManagementAccount>,
  options?: AxiosRequestConfig
) => Promise<CashManagementAccount>;

export type TestCashManagementSettlement = (
  id: UUID,
  options?: AxiosRequestConfig
) => Promise<any>;

export const getCashManagementAccounts: GetCashManagementAccounts = async (
  id,
  active,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: "/cash-management-accounts",
      params: {
        ...DEFAULT_PAGINATION,
        customer: id,
        active: active !== undefined ? active : undefined,
      },
      paramsSerializer,
    });
    const { data = {} } = response;

    return { list: data };
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const getCashManagementAccount: GetCashManagementAccount = async (
  id,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "GET",
      url: `/cash-management-accounts/${id}`,
    });
    const { data = {} } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const patchCashManagementAccount: PatchCashManagementAccount = async (
  id,
  account,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "PUT",
      url: `/cash-management-accounts/${id}`,
      data: account.cashManagementProviderAccount
        ? {
            ...account,
            ...returnProperCashManagementProviderAccountObject(
              account.cashManagementProviderAccount
            ),
          }
        : account,
    });
    const { data = {} } = response;

    return await getCashManagementAccount(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const postCashManagementAccount: PostCashManagementAccount = async (
  id,
  account,
  options
) => {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: "/cash-management-accounts",
      data: pickBy(
        {
          ...account,
          customer: id,
          ...returnProperCashManagementProviderAccountObject(
            account.cashManagementProviderAccount
          ),
          country: account.country.id,
          currency: account.currency.id,
          swiftBic: account.swiftBic,
        },
        identity
      ),
    });
    const { data = {} } = response;

    extendedApi.util.invalidateTags([TAGS.CASH_MANAGEMENT_ACCOUNTS]);

    return await getCashManagementAccount(data.id, options);
  } catch (error) {
    throw getErrorDetails(error);
  }
};

export const testCashManagementSettlement: TestCashManagementSettlement = async (
  id: UUID,
  options?: AxiosRequestConfig
) => {
  try {
    const response = await axios({
      ...options,
      method: "POST",
      url: `/customer/${id}/settlement/trigger-mock`,
    });
    const { data = {} } = response;

    return data;
  } catch (error) {
    throw getErrorDetails(error);
  }
};

type GetAccountsQuery = ObjectWithId;
type GetAccountsResponse = CashManagementAccount[];
type GetProviderBalanceQuery = ObjectWithId;
type GetProviderBalanceResponse = { amount: number };
type GetProvidersQuery = void;
type GetProvidersResponse = CashManagementProvider[];
type PostProductionSettlementQuery = {
  code: string;
} & ObjectWithId;
type PostProductionSettlementResponse = TranslationKeysFromBackend<
  typeof TRANSLATION_KEYS,
  "creatingSettlementInitiated" | "noUnsettledOperations"
>;
type GetSettlementScheduleConfigRequest = ObjectWithId;
type GetSettlementScheduleConfigResponse = SettlementScheduleConfig;

type GetSettlementBalanceRequest = ObjectWithId;
type GetSettlementBalanceResponse = { amount: number };

export const extendedApi = portalApi.injectEndpoints({
  endpoints: (builder) => ({
    getCMAccounts: builder.query<GetAccountsResponse, GetAccountsQuery>({
      query: ({ id }) => ({
        url: `/cash-management-accounts?customer=${id}&limit=1000&page=1&active=true`,
        method: "GET",
      }),
      providesTags: [TAGS.CASH_MANAGEMENT_ACCOUNTS],
    }),
    getProviderBalance: builder.query<
      GetProviderBalanceResponse,
      GetProviderBalanceQuery
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/provider-balance`,
        method: "GET",
      }),
    }),
    getProviders: builder.query<GetProvidersResponse, GetProvidersQuery>({
      query: () => ({
        url: "/cash-management/providers",
        method: "GET",
      }),
    }),
    getSettlementScheduleConfig: builder.query<
      GetSettlementScheduleConfigResponse,
      GetSettlementScheduleConfigRequest
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/settlement-schedule-config`,
        method: "GET",
      }),
    }),
    getSettlementBalance: builder.query<
      GetSettlementBalanceResponse,
      GetSettlementBalanceRequest
    >({
      query: ({ id }) => ({
        url: `/cash-management-accounts/${id}/settlement-balance`,
        method: "GET",
      }),
    }),
    postProductionSettlement: builder.mutation<
      PostProductionSettlementResponse,
      PostProductionSettlementQuery
    >({
      query: ({ code, id }) => ({
        url: `/customers/${id}/settlement/trigger`,
        method: "POST",
        body: { code },
      }),
      invalidatesTags: [TAGS.REFUNDS],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetCMAccountsQuery,
  useGetProviderBalanceQuery,
  useGetProvidersQuery,
  usePostProductionSettlementMutation,
  useLazyGetProviderBalanceQuery,
  useLazyGetSettlementScheduleConfigQuery,
  useLazyGetSettlementBalanceQuery,
} = extendedApi;
